"use client";

import Link from "next/link";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "@/lib/firestore-instrumented";
import { db } from "@/firebase/config";
import { showToast } from "@/components/ToastCenter";
import { buildChroniclePath, chronicleIdFor, formatRelativeDate, slugifyChronicle } from "@/lib/chronicles";
import ChronicleCard from "@/components/chronicles/ChronicleCard";
import ChronicleSectionMasthead from "@/components/chronicles/ChronicleSectionMasthead";

export default function HubChroniclesPanel({ hubSlug, hubDocId = "", user, canCreate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [scope, setScope] = useState("all");

  useEffect(() => {
    if (!hubSlug) return undefined;
    setLoading(true);
    setError("");
    const q = query(
      collection(db, "chronicles"),
      where("hubSlug", "==", hubSlug),
      orderBy("updatedAt", "desc"),
      limit(60)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Chronicles list failed:", err);
        setError("Could not load chronicles yet.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [hubSlug]);

  const rows = useMemo(() => {
    if (scope !== "mine" || !user?.uid) return items;
    return items.filter((item) => item.createdBy === user.uid);
  }, [items, scope, user?.uid]);

  return (
    <div className="space-y-6">
      <ChronicleSectionMasthead
        title="Chronicles"
        description="Build timeline-based stories for this space."
      />

      {(user || canCreate) ? (
        <div className="edge-mobile flex flex-col items-start gap-3 px-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-1">
          {user ? (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setScope("all")}
                className={`inline-flex items-center gap-2 rounded-full border border-[var(--border)] px-4 py-2 text-sm font-medium ${
                  scope === "all"
                    ? "bg-[var(--surface)] text-[var(--fg-primary)]"
                    : "text-[var(--fg-secondary)] hover:border-[var(--brand)] hover:text-[var(--brand)]"
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setScope("mine")}
                className={`inline-flex items-center gap-2 rounded-full border border-[var(--border)] px-4 py-2 text-sm font-medium ${
                  scope === "mine"
                    ? "bg-[var(--surface)] text-[var(--fg-primary)]"
                    : "text-[var(--fg-secondary)] hover:border-[var(--brand)] hover:text-[var(--brand)]"
                }`}
              >
                My chronicles
              </button>
            </div>
          ) : null}
          {canCreate ? (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--brand-foreground)] shadow-[var(--shadow-e1)] sm:ml-auto"
              onClick={() => setCreateOpen(true)}
            >
              Create Chronicle
            </button>
          ) : null}
        </div>
      ) : null}

      <section className="edge-mobile space-y-3">
        {loading ? (
          [0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-40 animate-pulse rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-e1)]"
            />
          ))
        ) : error ? (
          <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] px-4 py-6 text-sm text-red-600 shadow-[var(--shadow-e1)] sm:px-6">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] px-4 py-6 text-sm text-[var(--muted)] shadow-[var(--shadow-e1)] sm:px-6">
            {scope === "mine" ? "You have not created any chronicles yet." : "No chronicles yet in this space."}
            {canCreate ? " Start with the first one." : ""}
          </div>
        ) : (
          rows.map((item) => (
            <ChronicleCard
              key={item.id}
              title={item.name || item.slug}
              description={item.description || "No description yet."}
              incidentCountLabel={String(Number(item.incidentCount || 0))}
              updatedLabel={`Updated ${formatRelativeDate(item.updatedAt)}`}
              activityLabel={
                item.latestIncidentAt ? `Last incident ${formatRelativeDate(item.latestIncidentAt)}` : ""
              }
              href={buildChroniclePath(hubSlug, item.slug)}
              hubSlug={hubSlug}
              chronicleSlug={item.slug}
            />
          ))
        )}
      </section>

      <CreateChronicleModal
        open={createOpen}
        hubSlug={hubSlug}
        hubDocId={hubDocId}
        user={user}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}

function CreateChronicleModal({ open, onClose, hubSlug, hubDocId, user }) {
  const router = useRouter();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setSlug("");
    setDescription("");
    setSlugTouched(false);
    setSaving(false);
    setError("");
  }, [open]);

  useEffect(() => {
    if (slugTouched) return;
    setSlug(slugifyChronicle(name));
  }, [name, slugTouched]);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!user?.uid) {
        setError("Sign in to create a chronicle.");
        return;
      }
      const cleanedName = name.trim();
      const cleanedSlug = slugifyChronicle(slug);
      if (!cleanedName) {
        setError("Chronicle name is required.");
        return;
      }
      if (!cleanedSlug) {
        setError("Choose a valid slug.");
        return;
      }
      setSaving(true);
      setError("");
      try {
        const chronicleId = chronicleIdFor(hubSlug, cleanedSlug);
        const chronicleRef = doc(db, "chronicles", chronicleId);
        const slugCheckQ = query(
          collection(db, "chronicles"),
          where("hubSlug", "==", hubSlug),
          where("slug", "==", cleanedSlug),
          limit(1)
        );
        const existing = await getDocs(slugCheckQ);
        if (!existing.empty) {
          setError("That slug already exists in this space.");
          setSaving(false);
          return;
        }

        try {
          await setDoc(chronicleRef, {
            hubSlug,
            hubDocId: hubDocId || null,
            name: cleanedName,
            description: description.trim(),
            slug: cleanedSlug,
            createdBy: user.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            incidentCount: 0,
            latestIncidentAt: null,
          });
        } catch (writeErr) {
          console.error("Create chronicle failed at chronicle write:", {
            code: writeErr?.code,
            message: writeErr?.message,
            hubSlug,
            hubDocId: hubDocId || null,
            chronicleId,
          });
          throw writeErr;
        }

        try {
          await setDoc(doc(chronicleRef, "roles", user.uid), {
            uid: user.uid,
            role: "owner",
            joinedAt: serverTimestamp(),
            contributionCount: 0,
          });
        } catch (roleErr) {
          console.error("Create chronicle failed at role write:", {
            code: roleErr?.code,
            message: roleErr?.message,
            chronicleId,
            uid: user.uid,
          });
          throw roleErr;
        }

        showToast("Chronicle created.");
        onClose();
        router.push(buildChroniclePath(hubSlug, cleanedSlug));
      } catch (err) {
        console.error("Create chronicle failed:", {
          code: err?.code,
          message: err?.message,
        });
        setError(err?.message || "Could not create chronicle.");
      } finally {
        setSaving(false);
      }
    },
    [user?.uid, name, slug, hubSlug, hubDocId, description, onClose, router]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4 py-6 backdrop-blur-sm" onClick={onClose}>
      <section
        className="w-full max-w-lg rounded-2xl border border-[var(--border)] bg-[var(--card)] p-5"
        role="dialog"
        aria-modal="true"
        aria-label="Create chronicle"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-[var(--fg-primary)]">Create Chronicle</h3>
        <p className="mt-1 text-sm text-[var(--muted)]">Set up a timeline container for this space.</p>

        <form className="mt-4 space-y-4" onSubmit={onSubmit}>
          <div className="space-y-1">
            <label className="block text-sm font-medium">Name</label>
            <input
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={80}
              required
            />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium">Slug</label>
            <input
              className="input"
              value={slug}
              onChange={(event) => {
                setSlugTouched(true);
                setSlug(slugifyChronicle(event.target.value));
              }}
              maxLength={80}
              required
            />
            <p className="text-xs text-[var(--muted)]">Lowercase letters, numbers, and dashes only.</p>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium">Description</label>
            <textarea
              className="input min-h-[96px]"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              maxLength={280}
            />
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-sm text-[var(--fg-primary)] hover:bg-[var(--surface-2)]"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-[var(--radius)] bg-[var(--brand)] px-4 py-2 text-sm font-medium text-[var(--brand-foreground)] disabled:opacity-60"
            >
              {saving ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
